/* eslint-disable import/no-duplicates */

// Note the syntax of these imports from the date-fns library.
// If you import with the syntax: import { format } from "date-fns" the ENTIRE library
// will be included in your production bundle (even if you only use one function).
// This is because react-native does not support tree-shaking.
import { isValid } from 'date-fns';
import parseISO from 'date-fns/parseISO';

export const convertToDateWithMillisecondTimestamp = (timestamp: number) =>
  isTimestampInMilliseconds(timestamp) ? new Date(timestamp) : new Date(timestamp * 1000);

export const convertToMillisecondTimestamp = (timestamp: number) =>
  isTimestampInMilliseconds(timestamp) ? timestamp : timestamp * 1000;

export const parseDate = (date: Date | string | undefined) =>
  typeof date === 'string' ? parseISO(date) : isValid(date) ? (date as Date) : new Date();

export const isTimestampInMilliseconds = (timestamp: number): boolean => {
  // A reasonable threshold to differentiate between milliseconds and seconds
  // For example, any timestamp greater than January 1, 3000 in seconds
  const threshold = 10000000000; // 10 digits
  return timestamp > threshold;
};

/**
 * Parse a YYYY-MM-DD date string as **local** midnight.
 *
 * `new Date("2026-04-09")` and `parseISO("2026-04-09")` both interpret
 * date-only strings as UTC midnight (per ISO 8601). For users west of UTC
 * this causes off-by-one errors when the Date is displayed or compared
 * against local time.  This helper avoids that by constructing the Date
 * with the local-time `new Date(year, month, day)` overload.
 */
export const parseLocalDate = (dateStr: string): Date => {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d);
};

/**
 * Format a YYYY-MM-DD string for display using the local timezone.
 * Accepts an Intl.DateTimeFormat options object (defaults to short date).
 */
export const formatLocalDate = (
  dateStr: string,
  opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' },
): string => {
  if (!dateStr) return '—';
  return parseLocalDate(dateStr).toLocaleDateString('en-US', opts);
};

/**
 * Convert an epoch-seconds timestamp to a local YYYY-MM-DD string.
 * Unlike `toISOString().slice(0,10)` which returns a UTC date,
 * this returns the date in the user's local timezone.
 */
export const epochSecondsToLocalDateStr = (epochSec: number): string => {
  const dt = new Date(epochSec * 1000);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(
    dt.getDate(),
  ).padStart(2, '0')}`;
};
